// SS PWA — components/diagnostics/ReplayTimeline.tsx — 2026-06-03
//
// Chronological timeline for replay endpoint output. Operator view.

import type { ReplayResponse, ReplayEvent } from '@/lib/api/diagnostics';

function dot(status: ReplayEvent['status']): string {
  if (status === 'ok') return 'bg-emerald-500';
  if (status === 'warn') return 'bg-amber-500';
  if (status === 'error') return 'bg-red-500';
  if (status === 'skip') return 'bg-ink-300 dark:bg-[#475569]';
  return 'bg-ink-200 dark:bg-[#334155]';
}

export function ReplayTimeline({ replay }: { replay: ReplayResponse | null }) {
  if (!replay) {
    return (
      <div className="rounded-2xl border border-ink-200 bg-white p-3 text-sm text-ink-500 dark:border-[#1F2A37] dark:bg-[#0F172A] dark:text-[#94A3B8]">
        Run a replay to see a timeline here.
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-ink-200 bg-white dark:border-[#1F2A37] dark:bg-[#0F172A]">
      <div className="flex items-center justify-between border-b border-ink-100 px-3 py-2 dark:border-[#1F2A37]">
        <div className="text-sm font-semibold text-ink-900 dark:text-white">
          {replay.scope} · {replay.scope_id}
        </div>
        <div className="text-xs text-ink-500 dark:text-[#94A3B8]">
          {replay.events.length} events
        </div>
      </div>
      <ol className="divide-y divide-ink-100 dark:divide-[#1F2A37]">
        {replay.events.map((e, i) => (
          <li key={i} className="flex gap-3 px-3 py-2">
            <span className={`mt-1 inline-block size-2 rounded-full ${dot(e.status)}`} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-ink-900 dark:text-white">{e.step}</span>
                <span className="text-[10px] uppercase tracking-wide text-ink-400">{e.source}</span>
                <span className="ml-auto text-xs text-ink-400">
                  {new Date(e.ts).toLocaleString()}
                </span>
              </div>
              <div className="truncate text-xs text-ink-500 dark:text-[#94A3B8]">
                {Object.entries(e.detail)
                  .slice(0, 6)
                  .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
                  .join(' · ')}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
